/**
 * Deck statistics calculation logic, plus the missing-card/ownership
 * derivation shared by the deck builder and the public shared-deck page.
 */

import { ownedOfExactVariant, totalOwnedAcrossVariants, type VariantQuantityEntry } from './cardOwnership'

export const DECK_SIZE = 50

/** Line-item value for a deck card: unit market price times copies in the deck. */
export function deckLineValue(marketPrice: number, quantity: number): number {
  return marketPrice * quantity
}

export interface DeckStatsInput {
  card: {
    cost?: number | null
    color?: string | null
    cardType?: string | null
    marketPrice?: number | null
  }
  quantity: number
}

export interface LeaderInput {
  marketPrice?: number | null
}

export interface CalculatedDeckStats {
  costCurve: Record<number, number>
  // colorDistribution/typeBreakdown are keyed by color/card-type name — these
  // are data-keyed maps (stopPaths in lib/caseTransform.ts), so their keys
  // stay exactly as the server sends them (e.g. "Red", "Leader").
  colorDistribution: Record<string, number>
  typeBreakdown: Record<string, number>
  mainDeckCount: number
  validForPlay: boolean
  totalDeckValue: number
}

/**
 * Calculates deck statistics (cost curve, color distribution, type breakdown)
 * from an array of cards with quantities.
 */
export function calculateDeckStats(cards: DeckStatsInput[], leader?: LeaderInput | null): CalculatedDeckStats {
  const costCurve: Record<number, number> = {}
  const colorDistribution: Record<string, number> = {}
  const typeBreakdown: Record<string, number> = {}
  let totalDeckValue = 0

  // Include leader price in total deck value
  if (leader?.marketPrice) {
    totalDeckValue += leader.marketPrice
  }

  cards.forEach(({ card, quantity }) => {
    // Cost curve
    if (card.cost !== null && card.cost !== undefined) {
      costCurve[card.cost] = (costCurve[card.cost] || 0) + quantity
    }

    // Color distribution - handles multi-color cards (e.g., "Red;Blue")
    if (card.color) {
      card.color.split(';').forEach((c) => {
        const color = c.trim()
        colorDistribution[color] = (colorDistribution[color] || 0) + quantity
      })
    }

    // Type breakdown
    if (card.cardType) {
      typeBreakdown[card.cardType] = (typeBreakdown[card.cardType] || 0) + quantity
    }

    // Deck value
    if (card.marketPrice) {
      totalDeckValue += card.marketPrice * quantity
    }
  })

  const mainDeckCount = cards.reduce((sum, { quantity }) => sum + quantity, 0)

  return {
    costCurve,
    colorDistribution,
    typeBreakdown,
    mainDeckCount,
    validForPlay: mainDeckCount === DECK_SIZE,
    totalDeckValue,
  }
}

// ---------------------------------------------------------------------------
// Missing-card / ownership derivation (shared-deck + deck-builder views)
// ---------------------------------------------------------------------------

interface OwnershipCardLike {
  id: number
  cardImageId: string
  name: string
  imageUrl: string | null
}

export interface MissingCardDisplay<E extends VariantQuantityEntry = VariantQuantityEntry> {
  card: { id: number; name: string; imageUrl: string | null }
  needed: number
  ownedExact: number
  ownedTotal: number
  /** Owned copies of *other* art variants of this base card. Generic so
   * callers whose entries carry variant metadata (nested `card`) keep it —
   * the shared-deck page renders each alternate printing's name/set. */
  alternatives: E[]
}

/**
 * Builds the enriched "missing cards" list for a deck: for the leader + every
 * deck card, compares owned-exact vs owned-across-variants against the needed
 * quantity, and includes anything short of the requirement or covered only by
 * alternate art printings (so the UI can call those out separately).
 */
export function computeMissingDeckCards<E extends VariantQuantityEntry>(
  cards: Array<{ card: OwnershipCardLike; quantity: number }>,
  leader: OwnershipCardLike | null | undefined,
  quantitiesByBase: Record<string, E[]> | undefined,
): MissingCardDisplay<E>[] {
  if (!leader || !quantitiesByBase) return []

  const allCards = [{ card: leader, quantity: 1 }, ...cards]
  const result: MissingCardDisplay<E>[] = []

  allCards.forEach(({ card, quantity }) => {
    const ownedVariants = quantitiesByBase[card.cardImageId]
    const ownedExact = ownedOfExactVariant(ownedVariants, card.id)
    const ownedTotal = totalOwnedAcrossVariants(ownedVariants)

    // Include if truly missing OR has different versions
    if (ownedTotal < quantity || (ownedExact < quantity && ownedTotal >= quantity)) {
      result.push({
        card: { id: card.id, name: card.name, imageUrl: card.imageUrl },
        needed: quantity,
        ownedExact,
        ownedTotal,
        alternatives: (ownedVariants ?? []).filter((v) => v.cardId !== card.id),
      })
    }
  })

  return result
}
